import react, { useState, useEffect, useRef } from "react";
import { Box, styled, Button, Slider, Typography } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import { StyleSheet, View, Font, PDFViewer } from "@react-pdf/renderer";
import { ViewerButton } from "../ViewerButton";
import muiTheme from "../../themes/muiTheme";
import { SliderBar } from "../SliderBar";
import { pdfFile } from "../../setting";
import { Loading } from "../Loading";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const drawerWidth = 240;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
  pageWidth: number;
}>(({ theme, open, pageWidth }) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  backgroundColor: theme.palette.grey[100],
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: 0,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.up("md")]: {
      marginRight: drawerWidth,
    },
    [theme.breakpoints.down("md")]: {
      marginRight: 0,
    },
  }),
  position: "relative",
  overflowX: "auto",
}));

const EmptyContainer = styled(Box)<{
  width: number;
  height: number;
  isShowDrawer: boolean;
  isPortrait: boolean;
}>(({ width, height, isShowDrawer, isPortrait }) => ({
  height: getWidth(width, isShowDrawer, isPortrait) * 1.414,
  width: getWidth(width, isShowDrawer, isPortrait),
  border: "0px solid",
}));

function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimensions;
}

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

interface Props {
  currentPage: number;
  allPage: number;
  isShowDrawer: boolean;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setAllPages: React.Dispatch<React.SetStateAction<number>>;
}

export const PdfViewer = ({
  currentPage,
  allPage,
  isShowDrawer,
  setPage,
  setAllPages,
}: Props) => {
  const [prevPage, setPrevPage] = useState(currentPage - 1);
  const [subPage, setSubPage] = useState(currentPage);
  const { height, width } = useWindowDimensions();
  const [isPortrait, setIsPortrait] = useState(false);

  useEffect(
    () => setIsPortrait(getIsPortrait(window.screen.orientation.type)),
    []
  );

  useEffect(() => {
    if (!isPortrait) {
      if (currentPage % 2 !== 0) {
        setPrevPage(currentPage - 1);
        setSubPage(currentPage);
      } else {
        setPrevPage(currentPage);
        if (currentPage === allPage) {
          setSubPage(0);
          return;
        }
        setSubPage(currentPage + 1);
      }
    } else {
      setPrevPage(currentPage - 1);
      setSubPage(currentPage);
    }
  }, [currentPage]);

  window.addEventListener("orientationchange", () => {
    var orientation = window.screen.orientation;
    setIsPortrait(getIsPortrait(orientation.type));
  });

  const styles = StyleSheet.create({
    page: {
      width: getWidth(width, isShowDrawer, isPortrait),
      height: getWidth(width, isShowDrawer, isPortrait) * 1.414,
      borderRight: "1px solid",
      borderColor: muiTheme.palette.grey[300],
      fontFamily: "pretendard",
    },
  });
  return (
    <Main
      pageWidth={width}
      open={isShowDrawer}
      sx={{
        height: 1,
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          border: allPage > 1 ? "1px solid" : "0px solid",
          borderColor: muiTheme.palette.grey[300],
        }}
      >
        <Document
          file={process.env.PUBLIC_URL + `file/${pdfFile}.pdf`}
          loading={<Loading />}
          onLoadSuccess={({ numPages }) => {
            setPage(1);
            setAllPages(numPages);
          }}
        >
          {!isPortrait && (
            <View style={styles.page}>
              <Page
                noData={
                  <Box
                    component="img"
                    src={process.env.PUBLIC_URL + "img/empty_pdf.png"}
                    sx={{
                      height: getWidth(width, isShowDrawer, isPortrait) * 1.414,
                      width: getWidth(width, isShowDrawer, isPortrait),
                    }}
                  />
                }
                loading={
                  !isPortrait && (
                    <EmptyContainer
                      width={width}
                      height={height}
                      isShowDrawer={isShowDrawer}
                      isPortrait={isPortrait}
                    />
                  )
                }
                children={
                  <ViewerButton
                    viewerWidth={72}
                    viewerHeigth={
                      getWidth(width, isShowDrawer, isPortrait) * 1.414
                    }
                    leftPosition={-40}
                    icon={"../img/ic_prev.png"}
                    isPortrait={isPortrait}
                    setPage={() => {
                      if (currentPage === 1) {
                        return;
                      }
                      setPage(currentPage - 2);
                    }}
                  />
                }
                width={getWidth(width, isShowDrawer, isPortrait)}
                pageNumber={prevPage}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </View>
          )}
          <View>
            <Page
              noData={
                !isPortrait && (
                  <EmptyContainer
                    width={width}
                    height={height}
                    isShowDrawer={isShowDrawer}
                    isPortrait={isPortrait}
                  />
                )
              }
              loading={
                !isPortrait && (
                  <EmptyContainer
                    width={width}
                    height={height}
                    isShowDrawer={isShowDrawer}
                    isPortrait={isPortrait}
                  />
                )
              }
              children={
                <Box>
                  {isPortrait && currentPage > 1 && (
                    <ViewerButton
                      viewerWidth={72}
                      viewerHeigth={
                        getWidth(width, isShowDrawer, isPortrait) * 1.414
                      }
                      leftPosition={-40}
                      icon={"../img/ic_prev.png"}
                      isPortrait={isPortrait}
                      setPage={() => {
                        if (currentPage === 1) {
                          return;
                        }
                        setPage(currentPage - 1);
                      }}
                    />
                  )}
                  {currentPage < allPage && (
                    <ViewerButton
                      viewerWidth={72}
                      viewerHeigth={
                        getWidth(width, isShowDrawer, isPortrait) * 1.414
                      }
                      rightPosition={-40}
                      icon={"../img/ic_next.png"}
                      isPortrait={isPortrait}
                      setPage={() => {
                        if (currentPage >= allPage) {
                          return;
                        }
                        if (isPortrait) {
                          setPage(currentPage + 1);
                        } else {
                          setPage(currentPage + 2);
                        }
                      }}
                    />
                  )}
                </Box>
              }
              width={getWidth(width, isShowDrawer, isPortrait)}
              pageNumber={currentPage > allPage ? 0 : subPage}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </View>
        </Document>
      </Box>
      <SliderBar
        currentPage={currentPage}
        allPage={allPage}
        setPage={setPage}
      />
    </Main>
  );
};

function getWidth(width: number, isShowDrawer: boolean, isPortrait: boolean) {
  if (isPortrait) {
    if (width < 600) {
      return width * 0.89;
    } else if (width < 901) {
      return width * 0.87;
    }
    return width * 0.8;
  } else {
    if (isShowDrawer) console.log("@!!===>> width more than 800", width);
    if (isShowDrawer) {
      let viewerWidth = width - 400;
      return (viewerWidth / 2) * 0.78;
    }
    return (width / 2) * 0.65;
  }
}

function getIsPortrait(orientation: string) {
  return (
    orientation === "portrait-primary" || orientation === "portrait-secondary"
  );
}
